import { Base64 } from 'js-base64'
import { parse, stringify } from 'query-string'
import React, { useContext, useMemo, useRef } from 'react'
import { useHistory, useLocation, useRouteMatch } from 'react-router'
import { BaseLayout } from '..'
import { FeaturesEnum } from '../../../utils/features'
import { encodeStringForUrl } from '../../../utils/mediaLibrary'
import { addLeadingSlash, addTrailingSlash } from '../../../utils/urlHelpers'
import CustomPlanCTA from '../../components/CustomPlan'
import { FileType, MLViewType } from '../../constants'
import { useMemoizedFn } from '../../hooks/useMemoizedFn'
import { MLProvider, UserContext } from '../../providers'
import type { MediaCollectionsQueryParams, MediaLibaryQueryParams } from '../../providers/MLProvider'
import { PublicLinksInfoContext } from '../../providers/PublicLinksInfoProvider'

const MediaLibraryBaseLayout = ({
  children,
}: {
  children: React.ReactElement
}): React.ReactElement | null => {
  const { isPublicLink } = useContext(PublicLinksInfoContext)
  const history = useHistory()
  const location = useLocation()

  const initialStateRef = useRef({
    sort: 'DESC_CREATED',
    view: MLViewType.GRID,
    searchQuery: '',
    nameValue: '',
    semantic: '',
    semanticUrl: '',
    fileType: FileType.all,
    searchQueryJson: encodeStringForUrl(
      JSON.stringify({
        boolean: 'and',
        filterGroups: [],
      }),
    ),
    type: Base64.encode('type="file"', true),
    isFolderRestrictedSearch: 'false',
  })

  const queryFromUrl = useMemo(() => parse(location.search), [location.search])

  const queryParams: MediaLibaryQueryParams & MediaCollectionsQueryParams =
    useMemo(
      () => ({
        ...initialStateRef.current,
        ...queryFromUrl,
      }),
      [queryFromUrl],
    )

  const setQueryParams = useMemoizedFn(
    (
      newQuery: React.SetStateAction<
        Partial<MediaLibaryQueryParams & MediaCollectionsQueryParams>
      >,
    ): void => {
      const finalQueryParams = Object.entries({
        ...queryFromUrl,
        ...newQuery,
      }).reduce((acc, [key, value]) => {
        let val = value

        if (val !== null && newQuery[key]) {
          if (key === 'type') {
            val = Base64.encode(val, true)
          } else if (key === 'searchQueryJson') {
            val = encodeStringForUrl(JSON.stringify(val))
          }
        }

        if (initialStateRef.current[key] !== val) {
          acc[key] = val
        }

        return acc
      }, {})

      history.push({
        hash: location.hash,
        search: stringify(finalQueryParams, { skipNull: true }),
      })
    },
  )

  const { user } = useContext(UserContext)

  interface RouteParams {
    url: string
    folderPathBase64?: string
    mediaCollectionId?: string
  }

  const {
    // Default value - Base64 for "/"
    url,
    params: { folderPathBase64 = 'Lw==', mediaCollectionId },
  } = useRouteMatch<RouteParams>()

  const mediaCollectionRoute = useMemo(
    () => url.startsWith('/media-collections'),
    [url],
  )
  let folderPath: string | null = null

  try {
    folderPath = Base64.decode(folderPathBase64)
  } catch (e) {
    // todo: Handle error Better
    if ((window.env ?? 'dev') === 'dev') {
      console.error(
        '[INVALID_FOLDER_PATH]',
        'Folder path provided is invalid base64',
      )
    }
  }

  if (folderPath === null) {
    return null
  }

  return (
    isPublicLink ||
    user?.pricingPlan.featuresList.includes(
      FeaturesEnum.MEDIA_LIBRARY_STORAGE,
    ) ? (
      <MLProvider
        folderPath={folderPath}
        mediaCollectionId={mediaCollectionId}
        queryParams={queryParams}
        setQueryParams={setQueryParams}
        mediaCollectionRoute={mediaCollectionRoute}
      >
        <BaseLayout>{children}</BaseLayout>
      </MLProvider>
    ) : (
      <BaseLayout>
        <CustomPlanCTA feature={FeaturesEnum.MEDIA_LIBRARY_STORAGE} />
      </BaseLayout>
    )
  )
}

export default MediaLibraryBaseLayout
